// Print an account statement (cash/bank) on A4 — professional banking-style layout.
// Shows opening balance, every transaction with running balance, and closing balance.
import { API_BASE } from "@/lib/api";

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const escape = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
};

/**
 * account: { account_id, name, kind ('cash'|'bank'), opening_balance, balance, currency }
 * transactions: [{ txn_id, created_at, amount (+/-), balance_after, description, kind }]
 * Optional `period` { start, end, label } filters & labels the printout. If omitted,
 * the full transactions list is printed.
 */
export function printAccountStatement({ account, transactions, period = null, template, lang = "ar" }) {
  const isAR = lang === "ar";
  const dir = isAR ? "rtl" : "ltr";
  const t = (en, ar) => (isAR ? ar : en);
  const primary = template?.primary_color || "#D4AF64";

  // Sort ascending by date so the running balance reads naturally top→bottom.
  let txns = [...(transactions || [])].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  if (period && period.start && period.end) {
    txns = txns.filter((tx) => {
      const d = (tx.created_at || "").slice(0, 10);
      return d >= period.start && d <= period.end;
    });
  }

  // Opening balance for the printout = balance_after of the first tx minus that tx amount,
  // OR account.opening_balance when we're printing the whole history.
  const openingBalance = (() => {
    if (txns.length === 0) {
      return Number(account?.opening_balance || account?.balance || 0);
    }
    const first = txns[0];
    return Number(first.balance_after || 0) - Number(first.amount || 0);
  })();

  const totalIn = txns.filter((tx) => Number(tx.amount) > 0).reduce((s, tx) => s + Number(tx.amount), 0);
  const totalOut = txns.filter((tx) => Number(tx.amount) < 0).reduce((s, tx) => s + Number(tx.amount), 0);
  const closingBalance = txns.length > 0 ? Number(txns[txns.length - 1].balance_after || 0) : openingBalance;

  const rowsHtml = txns.map((tx, idx) => {
    const isDeposit = Number(tx.amount) > 0;
    const dateStr = (tx.created_at || "").slice(0, 16).replace("T", " ");
    return `
      <tr>
        <td class="num muted">${idx + 1}</td>
        <td class="num muted">${escape(dateStr)}</td>
        <td>${escape(tx.description || "—")}</td>
        <td class="num ${isDeposit ? "pos" : ""}">${isDeposit ? fmt(tx.amount) : "—"}</td>
        <td class="num ${!isDeposit ? "neg" : ""}">${!isDeposit ? "(" + fmt(Math.abs(tx.amount)) + ")" : "—"}</td>
        <td class="num strong">${fmt(tx.balance_after)}</td>
      </tr>
    `;
  }).join("");

  const acctTypeLbl = account?.kind === "bank" ? t("Bank Account", "حساب بنكي") : t("Cash Account", "صندوق نقد");
  const generatedStr = new Date().toLocaleString(isAR ? "ar-EG" : "en-US");

  const html = `
<!DOCTYPE html>
<html lang="${isAR ? "ar" : "en"}" dir="${dir}">
<head>
<meta charset="UTF-8" />
<title>${escape(account?.name || "")} — ${t("Statement", "كشف حساب")}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: ${isAR ? "'IBM Plex Sans Arabic', sans-serif" : "'Inter', -apple-system, sans-serif"};
    color: #111; background: #fff; font-size: 10.5pt;
  }
  .page { width: 100%; max-width: 186mm; margin: 0 auto; padding: 6mm 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 12px; border-bottom: 2px solid ${primary}; }
  .brand { display: flex; gap: 12px; align-items: center; }
  .logo { width: 64px; height: 64px; object-fit: contain; }
  .biz h1 { margin: 0 0 4px 0; font-size: 18pt; color: ${primary}; }
  .biz .meta { font-size: 9pt; color: #555; line-height: 1.5; }
  .doc-info { text-align: ${isAR ? "left" : "right"}; }
  .doc-info .doc-title { font-size: 14pt; color: ${primary}; font-weight: 700; margin-bottom: 4px; }
  .doc-info .row { font-size: 9.5pt; color: #333; line-height: 1.5; }
  .doc-info .label { color: #777; margin-${isAR ? "left" : "right"}: 6px; }

  .acct-card { margin-top: 16px; padding: 16px 18px; border: 2px solid ${primary}; border-radius: 8px; background: #fffaf0; display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 18px; align-items: center; }
  .acct-card .lbl { font-size: 9pt; color: #888; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 4px; }
  .acct-card .v { font-size: 13pt; color: #111; font-weight: 600; }
  .acct-card .balance .v { font-size: 22pt; color: ${primary}; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }

  .summary { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; margin-top: 14px; }
  .summary .card { border: 1px solid #ddd; border-radius: 6px; padding: 10px 12px; background: #fafafa; }
  .summary .card .lbl { font-size: 8.5pt; color: #777; text-transform: uppercase; letter-spacing: .5px; }
  .summary .card .v { font-size: 14pt; font-weight: 700; margin-top: 4px; font-variant-numeric: tabular-nums; }
  .summary .card.pos .v { color: #16a34a; }
  .summary .card.neg .v { color: #dc2626; }
  .summary .card.gold .v { color: ${primary}; }

  table.txns { width: 100%; border-collapse: collapse; margin-top: 16px; }
  table.txns th { background: ${primary}; color: #fff; padding: 7px 9px; font-size: 9.5pt; text-align: ${isAR ? "right" : "left"}; font-weight: 600; }
  table.txns th.num { text-align: ${isAR ? "left" : "right"}; }
  table.txns td { padding: 6px 9px; font-size: 10pt; border-bottom: 1px solid #eee; }
  table.txns td.num { text-align: ${isAR ? "left" : "right"}; font-variant-numeric: tabular-nums; white-space: nowrap; }
  table.txns td.strong { font-weight: 700; color: #111; }
  table.txns td.muted { color: #777; }
  table.txns td.pos { color: #16a34a; font-weight: 600; }
  table.txns td.neg { color: #dc2626; font-weight: 600; }
  table.txns tr:nth-child(even) td { background: #fbfaf7; }
  table.txns tr.opening td, table.txns tr.closing td { background: #f5efe1 !important; font-weight: 700; color: ${primary}; }

  .footer { margin-top: 20px; padding-top: 12px; border-top: 1px dashed #aaa; font-size: 9pt; color: #888; text-align: center; line-height: 1.6; }
  .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 28px; }
  .sig-box { border-top: 1px solid #888; padding-top: 6px; font-size: 9.5pt; color: #444; text-align: center; }

  .actions { margin-top: 10px; text-align: center; padding-top: 12px; }
  .actions button { background: ${primary}; color: #fff; border: 0; padding: 9px 18px; border-radius: 6px; cursor: pointer; font-size: 11pt; }

  @media print {
    .actions { display: none; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="brand">
        ${template?.logo_url ? `<img class="logo" src="${escape(resolveImg(template.logo_url))}" alt="" />` : ""}
        <div class="biz">
          <h1>${escape(isAR ? template?.business_name_ar : template?.business_name_en) || ""}</h1>
          <div class="meta">
            ${template?.phone ? `<div>${t("Phone", "هاتف")}: ${escape(template.phone)}</div>` : ""}
            ${template?.email ? `<div>${escape(template.email)}</div>` : ""}
            ${template?.tax_number ? `<div>${t("VAT", "الرقم الضريبي")}: ${escape(template.tax_number)}</div>` : ""}
          </div>
        </div>
      </div>
      <div class="doc-info">
        <div class="doc-title">${t("Account Statement", "كشف حساب")}</div>
        <div class="row"><span class="label">${t("Generated", "تاريخ الإصدار")}:</span> ${escape(generatedStr)}</div>
        ${period ? `<div class="row"><span class="label">${t("Period", "الفترة")}:</span> ${escape(period.start)} ${isAR ? "إلى" : "to"} ${escape(period.end)}</div>` : `<div class="row"><span class="label">${t("Period", "الفترة")}:</span> ${t("All time", "كل الفترات")}</div>`}
      </div>
    </div>

    <div class="acct-card">
      <div>
        <div class="lbl">${t("Account", "الحساب")}</div>
        <div class="v">${escape(account?.name || "—")}</div>
        <div style="font-size:9.5pt;color:#777;margin-top:4px;">${acctTypeLbl}${account?.currency ? ` · ${escape(account.currency)}` : ""}</div>
      </div>
      <div>
        <div class="lbl">${t("Opening Balance", "الرصيد الافتتاحي")}</div>
        <div class="v" style="font-variant-numeric: tabular-nums;">${fmt(openingBalance)}</div>
      </div>
      <div class="balance">
        <div class="lbl">${t("Closing Balance", "الرصيد الختامي")}</div>
        <div class="v">${fmt(closingBalance)}</div>
      </div>
    </div>

    <div class="summary">
      <div class="card pos">
        <div class="lbl">${t("Total Deposits", "إجمالي الإيداعات")}</div>
        <div class="v">${fmt(totalIn)}</div>
      </div>
      <div class="card neg">
        <div class="lbl">${t("Total Withdrawals", "إجمالي السحوبات")}</div>
        <div class="v">(${fmt(Math.abs(totalOut))})</div>
      </div>
      <div class="card gold">
        <div class="lbl">${t("Net Movement", "صافي الحركة")}</div>
        <div class="v">${fmt(totalIn + totalOut)}</div>
      </div>
      <div class="card">
        <div class="lbl">${t("Transactions", "عدد الحركات")}</div>
        <div class="v" style="color:#111;">${txns.length}</div>
      </div>
    </div>

    <table class="txns">
      <thead>
        <tr>
          <th style="width:5%;">#</th>
          <th style="width:16%;">${t("Date", "التاريخ")}</th>
          <th>${t("Description", "البيان")}</th>
          <th class="num" style="width:14%;">${t("Deposit", "إيداع")}</th>
          <th class="num" style="width:14%;">${t("Withdraw", "سحب")}</th>
          <th class="num" style="width:15%;">${t("Balance", "الرصيد")}</th>
        </tr>
      </thead>
      <tbody>
        <tr class="opening">
          <td>—</td>
          <td>${period ? escape(period.start) : "—"}</td>
          <td>${t("Opening balance brought forward", "رصيد مرحّل (افتتاحي)")}</td>
          <td class="num">—</td>
          <td class="num">—</td>
          <td class="num">${fmt(openingBalance)}</td>
        </tr>
        ${rowsHtml || `<tr><td colspan="6" style="text-align:center;color:#999;padding:20px;font-style:italic;">${t("No transactions in this period", "لا توجد حركات في هذه الفترة")}</td></tr>`}
        <tr class="closing">
          <td>—</td>
          <td>${period ? escape(period.end) : "—"}</td>
          <td>${t("Closing balance carried forward", "الرصيد الختامي")}</td>
          <td class="num">${fmt(totalIn)}</td>
          <td class="num">(${fmt(Math.abs(totalOut))})</td>
          <td class="num">${fmt(closingBalance)}</td>
        </tr>
      </tbody>
    </table>

    <div class="sig-grid">
      <div class="sig-box">${t("Prepared by — Accountant", "أعدّه — المحاسب")}</div>
      <div class="sig-box">${t("Verified by — Management", "اعتمده — الإدارة")}</div>
    </div>

    <div class="footer">
      ${t("Internal account statement. All amounts are in the account's recorded currency.", "كشف حساب داخلي. جميع المبالغ بعملة الحساب المسجلة.")}
    </div>

    <div class="actions">
      <button onclick="window.print()">${t("Print", "طباعة")}</button>
    </div>
  </div>
  <script>setTimeout(function(){ window.print(); }, 400);</script>
</body>
</html>
  `;

  const w = window.open("", "_blank", "width=950,height=1200");
  if (!w) return;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  w.location.href = url;
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } }, 60_000);
}
